import {
  BaseQueryFn,
  QueryDefinition,
  MutationDefinition,
  EndpointBuilder,
} from '@reduxjs/toolkit/query';

import type { IContactDocument } from 'interfaces/ContactDocument.interface';
import type { ICollectionApiResponse } from 'services/api/api.types';
import type {
  IGetContactDocumentArgs,
  IGetContactDocumentsCollectionArgs,
  IGetContactDocumentsCollectionResponse,
  IPutContactDocumentArgs,
} from './contactDocuments.api.type';
import {
  serializeFilters,
  serializeFlatObject,
} from 'services/helper/getPaginationParams.helper';
import { transformCollectionResponse } from 'services/teamHeroApi.helpers';

export const getContactDocuments = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IGetContactDocumentsCollectionArgs,
  BaseQueryFn,
  string,
  IGetContactDocumentsCollectionResponse
> =>
  build.query<
    IGetContactDocumentsCollectionResponse,
    IGetContactDocumentsCollectionArgs
  >({
    providesTags: ['ContactDocument'],
    query: (queryArg: IGetContactDocumentsCollectionArgs) => {
      const paginationParamObject = {
        page: queryArg.page,
        itemsPerPage: queryArg.itemsPerPage,
        pagination: queryArg.pagination,
        ...queryArg.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg.filters);
      return {
        url: `/api/contact_documents${paginationQuery}${filterQuery}`,
      };
    },
    transformResponse: (
      response: ICollectionApiResponse<IContactDocument>
    ): IGetContactDocumentsCollectionResponse =>
      transformCollectionResponse<IContactDocument>(response),
  });

export const getContactDocumentItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IGetContactDocumentArgs,
  BaseQueryFn,
  string,
  IContactDocument
> =>
  build.query<IContactDocument, IGetContactDocumentArgs>({
    providesTags: ['ContactDocument'],
    query: (queryArg: IGetContactDocumentArgs) => ({
      url: `api/contact_documents/${queryArg.id}`,
    }),
  });

export const putContactDocument = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IPutContactDocumentArgs,
  BaseQueryFn,
  string,
  IContactDocument
> =>
  build.mutation<IContactDocument, IPutContactDocumentArgs>({
    invalidatesTags: ['ContactDocument'],
    query: (queryArg) => ({
      url: `api/contact_documents/${queryArg.id}`,
      method: 'PATCH',
      body: queryArg.body,
    }),
  });
